/**
 * GLYDE Voice SDK - CDN Auto-Initialization
 *
 * Provides static methods for CDN usage and auto-initialization from data attributes.
 *
 * @example
 * // Via script tag
 * <script src="https://unpkg.com/@glydeunity/voice-sdk@latest/dist/glyde-chat.umd.js"></script>
 * <script>
 *   GlydeChat.init({
 *     publishableKey: 'pk_...',
 *     contextId: 'uuid...',
 *     unityBaseUrl: 'https://api.glydeunity.com'
 *   });
 * </script>
 *
 * @example
 * // Via data attributes (auto-init)
 * <script
 *   src="https://unpkg.com/@glydeunity/voice-sdk@latest/dist/glyde-chat.umd.js"
 *   data-publishable-key="pk_..."
 *   data-context-id="uuid..."
 *   data-auto-init="true"
 * ></script>
 */

import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ChatWidget } from '../components/ChatWidget';
import type { ChatWidgetConfig, ChatMode, WidgetPosition, ThemeMode, VoiceContextType, DisplayMode, WidgetDimensions } from '../core/types';
import { injectGlobalStyles } from '../ui/styles';

/** Injected at build time by Vite define (identifier replace). */
declare const __SDK_VERSION__: string;

/**
 * Active widget instance tracking
 */
let activeRoot: Root | null = null;
let activeContainer: HTMLElement | null = null;

/**
 * Track all active render roots for cleanup
 */
const renderRoots: Map<string, { root: Root; container: HTMLElement }> = new Map();

/**
 * GlydeChat - CDN entry point
 *
 * Provides static methods for initializing and controlling the chat widget.
 */
export const GlydeChat = {
  /**
   * Initialize the chat widget
   *
   * @param config - Widget configuration
   * @returns Control object for the widget
   */
  init(config: ChatWidgetConfig) {
    // Validate required fields
    if (!config.publishableKey && !config.apiKey && !config.authToken) {
      console.error('[GlydeChat] Authentication required: provide publishableKey, apiKey, or authToken');
      return null;
    }

    // Inject global styles
    const themeMode = config.theme === 'auto'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : (config.theme || 'light');
    injectGlobalStyles(themeMode);

    // Clean up existing widget if any
    if (activeRoot && activeContainer) {
      activeRoot.unmount();
      activeContainer.remove();
      activeRoot = null;
      activeContainer = null;
    }

    // Create container element
    const container = document.createElement('div');
    container.id = 'glyde-chat-widget';
    container.className = 'glyde-widget-root';
    document.body.appendChild(container);
    activeContainer = container;

    // Create React root and render
    const root = createRoot(container);
    activeRoot = root;

    root.render(
      React.createElement(ChatWidget, {
        ...config,
        initialExpanded: false,
      })
    );

    console.log('[GlydeChat] Widget initialized with unityBaseUrl:', config.unityBaseUrl);

    // Return control object
    return {
      /**
       * Destroy the widget
       */
      destroy() {
        GlydeChat.destroy();
      },

      /**
       * Update widget configuration
       */
      update(newConfig: Partial<ChatWidgetConfig>) {
        if (activeRoot) {
          activeRoot.render(
            React.createElement(ChatWidget, {
              ...config,
              ...newConfig,
            })
          );
        }
      },
    };
  },

  /**
   * Destroy the current widget instance
   */
  destroy() {
    // Destroy main widget
    if (activeRoot && activeContainer) {
      activeRoot.unmount();
      activeContainer.remove();
      activeRoot = null;
      activeContainer = null;
      console.log('[GlydeChat] Widget destroyed');
    }

    // Destroy any render instances
    renderRoots.forEach(({ root }, key) => {
      root.unmount();
      // Don't remove the container for render() calls - user provided it
      renderRoots.delete(key);
    });
  },

  /**
   * Render widget into a specific container (non-floating)
   *
   * @param container - DOM element or selector
   * @param config - Widget configuration
   */
  render(container: HTMLElement | string, config: ChatWidgetConfig) {
    const element = typeof container === 'string'
      ? document.querySelector(container)
      : container;

    if (!element) {
      console.error('[GlydeChat] Container not found:', container);
      return null;
    }

    const containerId = typeof container === 'string' ? container : element.id || `glyde-render-${Date.now()}`;

    // Clean up existing render in this container
    if (renderRoots.has(containerId)) {
      const existing = renderRoots.get(containerId);
      if (existing) {
        existing.root.unmount();
        renderRoots.delete(containerId);
      }
    }

    // Inject global styles
    const themeMode = config.theme === 'auto'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : (config.theme || 'light');
    injectGlobalStyles(themeMode);

    // Create React root and render
    const root = createRoot(element);

    // Track this render root
    renderRoots.set(containerId, { root, container: element as HTMLElement });

    root.render(
      React.createElement(ChatWidget, {
        ...config,
        container: element as HTMLElement,
      })
    );

    console.log('[GlydeChat] Widget rendered in container:', containerId);

    return {
      destroy() {
        root.unmount();
        renderRoots.delete(containerId);
        // Clear the container content
        (element as HTMLElement).innerHTML = '';
        console.log('[GlydeChat] Rendered widget destroyed');
      },
      update(newConfig: Partial<ChatWidgetConfig>) {
        root.render(
          React.createElement(ChatWidget, {
            ...config,
            ...newConfig,
            container: element as HTMLElement,
          })
        );
      },
    };
  },

  /**
   * Check if the SDK is loaded and ready
   */
  isReady(): boolean {
    return true;
  },

  /**
   * Get SDK version (replaced at build time via Vite define).
   */
  get version(): string {
    return __SDK_VERSION__;
  },
};

/**
 * If the page URL has ?t=TOKEN (trusted-app screen redirect), verify the token and return
 * { contextType: 'screening', contextId }. Otherwise returns null.
 * Uses unityBaseUrl for the verify API call.
 *
 * SECURITY: The verify endpoint requires:
 * - x-publishable-key header (from SDK config)
 * - x-source-domain header (hardcoded to 'screen.glydetalent.com' - the only app that creates screen tokens)
 *
 * The server uses x-source-domain to look up the trusted app's sha256Key and verify the JWT signature.
 *
 * @param unityBaseUrl - Base URL for the Unity API
 * @param publishableKey - Client's publishable key for authentication
 */
async function resolveScreenTokenFromUrl(
  unityBaseUrl: string,
  publishableKey?: string
): Promise<{ contextType: 'screening'; contextId: string } | null> {
  if (typeof window === 'undefined' || !window.location || !window.location.search) return null;
  const params = new URLSearchParams(window.location.search);
  const t = params.get('t');
  if (!t) return null;

  const url = `${unityBaseUrl.replace(/\/$/, '')}/api/unity/voice/screen/verify?t=${encodeURIComponent(t)}`;

  // Build headers - x-source-domain is hardcoded as screen.glydetalent.com is the only app that creates screen tokens
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-source-domain': 'screen.glydetalent.com'
  };

  // Add publishable key if provided
  if (publishableKey) {
    headers['x-publishable-key'] = publishableKey;
  }

  try {
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers
    });
    const data = await res.json();
    if (res.ok && data?.success && data?.data?.contextType === 'screening' && data?.data?.contextId) {
      return { contextType: 'screening', contextId: data.data.contextId };
    }
    console.warn('[GlydeChat] Screen token verify failed:', res.status, data);
    return null;
  } catch (err) {
    console.warn('[GlydeChat] Screen token verify request failed:', err);
    return null;
  }
}

/**
 * Parse a dimensions string like "width: 100%, height: 100%" or "width: 500, height: 600"
 * into WidgetDimensions. Used by data-dimensions attribute.
 *
 * @param s - Attribute value (e.g. "width: 100%, height: 100%")
 * @returns Parsed dimensions or undefined if nothing valid
 */
function parseDimensionsString(s: string): WidgetDimensions {
  const out: WidgetDimensions = {};
  const parts = s.split(/\s*,\s*/);
  const keyMap: Record<string, keyof WidgetDimensions> = {
    width: 'width',
    height: 'height',
    maxwidth: 'maxWidth',
    'max-width': 'maxWidth',
    maxheight: 'maxHeight',
    'max-height': 'maxHeight',
  };
  for (const part of parts) {
    const match = part.match(/^\s*(width|height|maxWidth|maxHeight|max-width|max-height)\s*:\s*(.+)\s*$/i);
    if (match) {
      const key = keyMap[match[1].toLowerCase().replace(/-/g, '')] ?? (match[1] as keyof WidgetDimensions);
      const raw = match[2].trim();
      const num = Number(raw);
      out[key] = isNaN(num) ? raw : num;
    }
  }
  return out;
}

/**
 * Auto-initialize from script tag data attributes
 *
 * Auto-init triggers when:
 * - data-auto-init="true" is explicitly set, OR
 * - data-publishable-key, data-api-key, or data-auth-token is present
 *
 * When the URL contains ?t=TOKEN (trusted-app screen redirect), the SDK automatically
 * calls the screen verify API and applies contextType=screening and contextId=application_uuid
 * before init, so a minimal implementation (script tag with data-publishable-key only) works.
 *
 * For inline/embed display, set data-container-id to the id of the target element (e.g. data-container-id="verstela-voice-container")
 * so the widget mounts inside that element instead of appending to body.
 *
 * To disable auto-init, set data-auto-init="false"
 */
function autoInit() {
  // Prefer the script that is executing (currentScript); fallback to last script with glyde-chat in src
  const currentScript =
    (typeof document !== 'undefined' && document.currentScript as HTMLScriptElement | null) ||
    (() => {
      const scripts = document.querySelectorAll('script[src*="glyde-chat"]');
      return scripts.length ? (scripts[scripts.length - 1] as HTMLScriptElement) : null;
    })();

  if (!currentScript) {
    console.log('[GlydeChat] No script tag found for auto-init');
    return;
  }

  // Check for explicit disable
  const autoInitAttr = currentScript.getAttribute('data-auto-init');
  if (autoInitAttr === 'false') {
    console.log('[GlydeChat] Auto-init disabled via data-auto-init="false"');
    return;
  }

  // Parse configuration from data attributes
  const publishableKey = currentScript.getAttribute('data-publishable-key') || undefined;
  const apiKey = currentScript.getAttribute('data-api-key') || undefined;
  const authToken = currentScript.getAttribute('data-auth-token') || undefined;

  // Auto-init if explicit flag OR if auth credentials are provided
  const hasAuth = publishableKey || apiKey || authToken;
  const explicitAutoInit = autoInitAttr === 'true';

  if (!hasAuth && !explicitAutoInit) {
    console.log('[GlydeChat] Auto-init skipped: no authentication data attributes found. Call GlydeChat.init() manually.');
    return;
  }

  if (!hasAuth) {
    console.warn('[GlydeChat] Auto-init skipped: no authentication provided (publishableKey, apiKey, or authToken required)');
    return;
  }

  const unityBaseUrl = currentScript.getAttribute('data-unity-url') || 'https://api.glydeunity.com';

  // Optional container for inline/embed: mount into this element instead of appending to body
  const containerId = currentScript.getAttribute('data-container-id') || undefined;

  // Parse dimensions: support data-width/height/max-* or single data-dimensions="width: 100%, height: 100%"
  const widthAttr = currentScript.getAttribute('data-width');
  const heightAttr = currentScript.getAttribute('data-height');
  const maxWidthAttr = currentScript.getAttribute('data-max-width');
  const maxHeightAttr = currentScript.getAttribute('data-max-height');
  const dimensionsAttr = currentScript.getAttribute('data-dimensions');

  let dimensions: WidgetDimensions | undefined;
  if (widthAttr || heightAttr || maxWidthAttr || maxHeightAttr) {
    dimensions = {
      width: widthAttr ? (isNaN(Number(widthAttr)) ? widthAttr : Number(widthAttr)) : undefined,
      height: heightAttr ? (isNaN(Number(heightAttr)) ? heightAttr : Number(heightAttr)) : undefined,
      maxWidth: maxWidthAttr ? (isNaN(Number(maxWidthAttr)) ? maxWidthAttr : Number(maxWidthAttr)) : undefined,
      maxHeight: maxHeightAttr ? (isNaN(Number(maxHeightAttr)) ? maxHeightAttr : Number(maxHeightAttr)) : undefined,
    };
  } else if (dimensionsAttr && dimensionsAttr.trim()) {
    dimensions = parseDimensionsString(dimensionsAttr);
  }

  const baseConfig: ChatWidgetConfig = {
    publishableKey,
    apiKey,
    authToken,
    contextId: currentScript.getAttribute('data-context-id') || undefined,
    unityBaseUrl,
    contextType: (currentScript.getAttribute('data-context-type') as VoiceContextType) || 'discovery',
    defaultMode: (currentScript.getAttribute('data-default-mode') as ChatMode) || 'voice',
    position: (currentScript.getAttribute('data-position') as WidgetPosition) || 'bottom-right',
    theme: (currentScript.getAttribute('data-theme') as ThemeMode) || 'light',
    allowModeSwitch: currentScript.getAttribute('data-allow-mode-switch') !== 'false',
    displayMode: (currentScript.getAttribute('data-display-mode') as DisplayMode) || 'floating',
    dimensions,
    showHeader: currentScript.getAttribute('data-show-header') !== 'false',
    allowClose: currentScript.getAttribute('data-allow-close') !== 'false',
  };

  const runInit = (config: ChatWidgetConfig) => {
    console.log('[GlydeChat] Auto-initializing with config:', {
      hasPublishableKey: !!config.publishableKey,
      hasApiKey: !!config.apiKey,
      hasAuthToken: !!config.authToken,
      contextId: config.contextId,
      contextType: config.contextType,
      defaultMode: config.defaultMode,
      displayMode: config.displayMode,
      position: config.position,
      theme: config.theme,
      dimensions: config.dimensions,
      containerId: containerId || null,
    });
    const displayMode = config.displayMode || 'floating';
    if (containerId && (displayMode === 'inline' || displayMode === 'modal' || displayMode === 'mobile')) {
      const selector = containerId.startsWith('#') ? containerId : `#${containerId}`;
      const el = document.querySelector(selector);
      if (el) {
        GlydeChat.render(selector, config);
        return;
      }
      console.warn('[GlydeChat] data-container-id element not found:', selector, '- falling back to init()');
    }
    GlydeChat.init(config);
  };

  const onReady = async () => {
    let config = baseConfig;
    // Pass publishableKey for authentication when verifying screen tokens
    const screenContext = await resolveScreenTokenFromUrl(unityBaseUrl, publishableKey);
    if (screenContext) {
      config = { ...config, contextType: screenContext.contextType.trim() as VoiceContextType, contextId: screenContext.contextId.trim() as string };
      console.log('[GlydeChat] Screen token applied: contextType=', screenContext.contextType, 'contextId=', screenContext.contextId);
    }
    runInit(config);
  };

  // Run when DOM is ready so document.body exists for the widget. Use DOMContentLoaded if still loading,
  // otherwise setTimeout(0) so we run after the current script and any inline scripts (reliable on-load trigger).
  const scheduleInit = () => {
    onReady();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
  } else {
    setTimeout(scheduleInit, 0);
  }
}

// Auto-initialize when loaded
if (typeof window !== 'undefined') {
  // Expose GlydeChat globally
  (window as unknown as Record<string, unknown>).GlydeChat = GlydeChat;

  console.log('[GlydeChat] SDK version:', GlydeChat.version);

  // Attempt auto-init
  autoInit();
}

export default GlydeChat;
